<script lang="ts">
	import type { Device } from 'src/types/Device';
	import { isPeripheralDevice } from 'src/helpers/addons/addon_device_helpers';

	// Stores for peripheral sensor
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices_config } from 'src/configs/devices';
	import { getDeviceChartsConfig } from 'src/helpers/statistics/config/device_chart_config';
	import { checkIsCriticalEvent } from 'src/helpers/device_data_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		createGen1StatusLabels,
		createGen1CombinedLabelList,
		extractGen1MainLabel,
		getGen1DeviceStatuses,
		canShowGen1LabelList,
	} from 'src/components/Sidepanel/SidePanelLayoutProvider/SidePanelDeviceLayout/Devices/Shelly/Gen1/gen1-main-helpers';
	import { ParameterCardV2 } from '@Shelly/shelly-design-system';
	import { mapIconToThin } from 'src/helpers/new-layout-helpers';
	import ThumbLayerChart from 'src/components/Chart/LayerChart/ThumbLayerChart.svelte';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import DeviceViewStateWrapper from 'src/components/Sidepanel/DeviceViews/DeviceViewStateWrapper.svelte';
	import { useNavigateToHistoryStatisticsTab } from 'src/components/Sidepanel/useNavigateToHistoryStatisticsTab';

	export let device: Device;

	// Check if this is an extracted peripheral sensor device
	$: isPeripheralSensor = isPeripheralDevice(device?.type) && device?.category === 'sensor';

	// Peripheral sensor logic (copied from SensorAdvancedView)
	import { real_id, is_gen_2 } from 'src/helpers/interface_helpers';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	import LightAdvancedView from 'src/components/Sidepanel/DeviceViews/Light/LightAdvancedView.svelte';
	import DeviceLabelList from 'src/components/Sidepanel/DeviceViews/DeviceLabelList.svelte';

	const { navigateToHistoryStatisticsTab } = useNavigateToHistoryStatisticsTab();
	$: deviceConfig = devices_config?.[device?.type];
	const isGen2 = is_gen_2(device);
	let timerLabels: Record<string, any> = {};
	$: statusStore = getDeviceStatus(device?.id);
	$: settingsStore = getDeviceSettings(device?.id);
	$: status = $statusStore;
	$: settings = $settingsStore;

	// Use status_configurator for peripheral devices (it handles them correctly)
	// For Gen2 devices, use status_configurator; for Gen1, use getGen1DeviceStatuses
	$: _statusesGen2 =
		isGen2 && status?.online && deviceConfig ? status_configurator(status, device, settings) : [];
	$: _statusesGen1 = !isGen2 ? getGen1DeviceStatuses(status, device, settings, deviceConfig) : [];
	$: _statuses = isGen2 ? _statusesGen2 : _statusesGen1;

	$: labelListGen2 = Array.isArray(_statusesGen2)
		? _statusesGen2.filter((e) => {
				return 'undefined' === typeof e.main && e.name !== 'state';
			})
		: [];
	$: labelListGen1 = !isGen2 ? createGen1StatusLabels(_statusesGen1, device) : [];
	$: labelList = isGen2 ? labelListGen2 : labelListGen1;

	$: combinedLabelListGen2 = [...Object.values(timerLabels), ...labelListGen2];
	$: combinedLabelListGen1 = createGen1CombinedLabelList(timerLabels, labelListGen1);
	$: combinedLabelList = isGen2 ? combinedLabelListGen2 : combinedLabelListGen1;

	$: mainLabel = (() => {
		if (isGen2) {
			// For peripheral devices, look for mainLabel property; for regular devices, look for main property
			if (isPeripheralSensor) {
				return Array.isArray(_statusesGen2) ? _statusesGen2.find((s) => s.mainLabel) : undefined;
			}
			return Array.isArray(_statusesGen2) ? _statusesGen2.find((s) => s.main) : undefined;
		}
		return extractGen1MainLabel(_statusesGen1);
	})();

	$: deviceChartsConfig = device ? getDeviceChartsConfig(device) : [];
	const handleOnClickThumbChart = (event: CustomEvent | Event) => {
		navigateToHistoryStatisticsTab();
	};
</script>

{#if isPeripheralSensor}
	<!-- SensorAdvancedView content for peripheral sensor devices -->
	<DeviceViewStateWrapper {device}>
		<div
			slot="staticTop"
			class="label-list-wrapper mb-s d-flex flex-direction-column justify-content-center"
		>
			{#if (isGen2 && labelList.length) || (!isGen2 && canShowGen1LabelList(device, status))}
				<DeviceLabelList labelList={combinedLabelList} />
			{/if}
			<DeviceNotificationLabel deviceId={device?.id} />
		</div>

		<div slot="content" class="flex-1"></div>

		<div class="flex-full-height justify-content-end" slot="staticBottom">
			{#if deviceChartsConfig.length}
				<ThumbLayerChart
					class={mainLabel ? 'mb-s' : ''}
					{device}
					on:click={handleOnClickThumbChart}
				/>
			{/if}
			{#if mainLabel}
				{@const isCriticalEvent = checkIsCriticalEvent(mainLabel.value)}
				<ParameterCardV2
					class="mb-s"
					icon={mapIconToThin(mainLabel.icon)}
					iconColor={mainLabel?.iconColor || ''}
					title={capFirstChar(mainLabel.value)}
					description={capFirstChar(mainLabel.translation || mainLabel.peripheralType || '')}
					backgroundColor={isCriticalEvent ? '--color-danger-10' : '--color-0'}
				/>
			{/if}
		</div>
	</DeviceViewStateWrapper>
{:else}
	<!-- Light views for main device -->
	<!-- For now we only use the advanced view for light devices. However the logic for view change remains, if we need to use it, for future changes. -->
	<LightAdvancedView {device} />
{/if}
